<template>
  <div class="bg-white rounded-xl shadow-lg p-6">
    <h2 class="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
      <span>📊</span>
      Tendencias Semanales
    </h2>

    <!-- Tendencia General -->
    <div :class="getTendenciaClass(tendencias.tendencia_general.direccion)" class="rounded-lg p-4 mb-6">
      <div class="flex items-center gap-3">
        <span class="text-3xl">{{ getTendenciaIcono(tendencias.tendencia_general.direccion) }}</span>
        <div class="flex-1">
          <div class="flex items-center gap-2 mb-1">
            <h3 class="font-semibold text-gray-900">{{ tendencias.tendencia_general.direccion }}</h3>
            <span :class="getVariacionClass(tendencias.tendencia_general.variacion_porcentual)" class="px-2 py-0.5 rounded-full text-xs font-semibold">
              {{ tendencias.tendencia_general.variacion_porcentual > 0 ? '+' : '' }}{{ tendencias.tendencia_general.variacion_porcentual.toFixed(1) }}%
            </span>
          </div>
          <p class="text-sm text-gray-700">{{ tendencias.tendencia_general.mensaje }}</p>
        </div>
      </div>
    </div>

    <!-- Comparativa Primera vs Última Semana -->
    <div class="grid grid-cols-2 gap-4 mb-6">
      <div class="bg-blue-50 rounded-lg p-4 border border-blue-200">
        <p class="text-sm text-gray-600 mb-1">Primera Semana</p>
        <p class="text-3xl font-bold text-blue-600">{{ tendencias.tendencia_general.ventas_primera_semana }}</p>
        <p class="text-xs text-gray-500">ventas</p>
      </div>
      <div class="bg-purple-50 rounded-lg p-4 border border-purple-200">
        <p class="text-sm text-gray-600 mb-1">Última Semana</p>
        <p class="text-3xl font-bold text-purple-600">{{ tendencias.tendencia_general.ventas_ultima_semana }}</p>
        <p class="text-xs text-gray-500">ventas</p>
      </div>
    </div>

    <!-- Datos Semanales -->
    <div class="space-y-3">
      <h3 class="font-semibold text-gray-900 mb-3">Desglose Semanal</h3>
      <div
        v-for="(semana, index) in tendencias.datos_semanales"
        :key="`${semana.año}-${semana.semana}`"
        class="border rounded-lg p-4 hover:shadow-md transition-all"
      >
        <div class="flex items-center justify-between mb-3">
          <div>
            <h4 class="font-semibold text-gray-900">Semana {{ semana.semana }} - {{ semana.año }}</h4>
            <p class="text-sm text-gray-600">{{ formatearMoneda(semana.valor) }}</p>
          </div>
          <div class="text-right">
            <div class="text-2xl font-bold text-gray-900">{{ semana.ventas }}</div>
            <div class="text-xs text-gray-500">ventas</div>
          </div>
        </div>

        <!-- Completadas -->
        <div class="flex items-center gap-2 mb-2">
          <span class="text-sm text-gray-600 w-24">Completadas:</span>
          <div class="flex-1 bg-gray-200 rounded-full h-4">
            <div 
              class="bg-gradient-to-r from-green-400 to-green-600 h-4 rounded-full transition-all duration-500 flex items-center justify-end pr-2"
              :style="`width: ${semana.tasa_completado}%`"
            >
              <span class="text-white text-xs font-semibold">{{ semana.completadas }}</span>
            </div>
          </div>
          <span class="text-sm font-semibold text-gray-700 w-12 text-right">{{ semana.tasa_completado.toFixed(0) }}%</span>
        </div>

        <!-- Indicador de cambio -->
        <div v-if="index > 0" class="flex items-center gap-2 text-xs">
          <span class="text-gray-600">Vs semana anterior:</span>
          <span :class="getComparativoClass(semana.ventas, tendencias.datos_semanales[index - 1].ventas)" class="font-semibold">
            {{ getComparativoTexto(semana.ventas, tendencias.datos_semanales[index - 1].ventas) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TendenciasSemanales',
  props: {
    tendencias: {
      type: Object,
      required: true
    }
  },
  methods: {
    getTendenciaClass(direccion) {
      switch (direccion) {
        case 'ASCENDENTE':
          return 'bg-green-100 border-2 border-green-300';
        case 'DESCENDENTE':
          return 'bg-red-100 border-2 border-red-300';
        case 'ESTABLE':
          return 'bg-blue-100 border-2 border-blue-300';
        default:
          return 'bg-gray-100 border-2 border-gray-300';
      }
    },
    getTendenciaIcono(direccion) {
      switch (direccion) {
        case 'ASCENDENTE':
          return '📈';
        case 'DESCENDENTE':
          return '📉';
        case 'ESTABLE':
          return '➡️';
        default:
          return '📊';
      }
    },
    getVariacionClass(variacion) {
      if (variacion > 0) return 'bg-green-100 text-green-800';
      if (variacion < 0) return 'bg-red-100 text-red-800';
      return 'bg-gray-100 text-gray-800';
    },
    formatearMoneda(valor) {
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(valor);
    },
    getComparativoTexto(actual, anterior) {
      const diferencia = actual - anterior;
      const porcentaje = ((diferencia / anterior) * 100).toFixed(1);
      if (diferencia > 0) return `+${diferencia} (${porcentaje}%)`;
      if (diferencia < 0) return `${diferencia} (${porcentaje}%)`;
      return 'Sin cambio';
    },
    getComparativoClass(actual, anterior) {
      const diferencia = actual - anterior;
      if (diferencia > 0) return 'text-green-600';
      if (diferencia < 0) return 'text-red-600';
      return 'text-gray-600';
    }
  }
};
</script>
